/**
 * Lesson page — structured as a high-end academic textbook chapter.
 *
 * Includes:
 * - Textbook chapter header with unboxed metadata
 * - Theorem and formula definition callout cards
 * - Step-by-step worked examples with cognitive levels
 * - Interactive key concept highlighting
 * - Sticky study dock for launching Socratic dialogue or practice quiz
 */
import { useCallback, useRef, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { apiGet } from "../../api/client";
import { Figure } from "../../components/Figure";
import { MathText } from "../../components/Math";
import { WorkedExample } from "../../components/WorkedExample";
import type { Lesson } from "../../types";

function stripDiagrams(s: string): string {
  return (s ?? "").replace(/\[Diagram[\s\S]*?\]/g, "").replace(/\n{3,}/g, "\n\n").trim();
}

function highlightTerm(container: HTMLElement, term: string): number {
  let matchCount = 0;
  const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
  const textNodes: Text[] = [];
  while (walker.nextNode()) {
    textNodes.push(walker.currentNode as Text);
  }

  for (const node of textNodes) {
    if (!regex.test(node.textContent || "")) continue;
    regex.lastIndex = 0;

    const parent = node.parentNode;
    if (!parent) continue;
    if ((parent as HTMLElement).closest?.(".katex")) continue;

    const frag = document.createDocumentFragment();
    let lastIdx = 0;
    const text = node.textContent || "";
    let match: RegExpExecArray | null;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIdx) {
        frag.appendChild(document.createTextNode(text.slice(lastIdx, match.index)));
      }
      const mark = document.createElement("mark");
      mark.className = "keyword-highlight";
      mark.textContent = match[1];
      frag.appendChild(mark);
      matchCount++;
      lastIdx = regex.lastIndex;
    }
    if (lastIdx < text.length) {
      frag.appendChild(document.createTextNode(text.slice(lastIdx)));
    }

    parent.replaceChild(frag, node);
  }

  return matchCount;
}

function clearHighlights(container: HTMLElement) {
  const marks = container.querySelectorAll("mark.keyword-highlight");
  marks.forEach((mark) => {
    const parent = mark.parentNode;
    if (!parent) return;
    parent.replaceChild(document.createTextNode(mark.textContent || ""), mark);
    parent.normalize();
  });
}

export function LessonPage() {
  const { cid, lid } = useParams<{ cid: string; lid: string }>();
  const { data: lesson, isLoading, error } = useQuery({
    queryKey: ["lesson", cid, lid],
    queryFn: () => apiGet<Lesson>(`/api/curricula/${cid}/lessons/${lid}`),
  });

  const [activeKeyword, setActiveKeyword] = useState<{ sectionId: string; concept: string } | null>(null);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  const handleConceptClick = useCallback((sectionId: string, concept: string) => {
    if (activeKeyword?.sectionId === sectionId && activeKeyword?.concept === concept) {
      const container = sectionRefs.current[sectionId];
      if (container) clearHighlights(container);
      setActiveKeyword(null);
      return;
    }

    Object.values(sectionRefs.current).forEach((el) => {
      if (el) clearHighlights(el);
    });

    const container = sectionRefs.current[sectionId];
    if (container) {
      const count = highlightTerm(container, concept);
      setActiveKeyword({ sectionId, concept });

      if (count > 0) {
        const firstMark = container.querySelector("mark.keyword-highlight");
        if (firstMark) {
          firstMark.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    }
  }, [activeKeyword]);

  if (isLoading)
    return (
      <div className="lesson-page">
        <p className="text-muted">Loading lesson chapter...</p>
      </div>
    );
  if (error || !lesson)
    return (
      <div className="lesson-page">
        <p className="error-message">Lesson not found.</p>
      </div>
    );

  const totalObjectives = lesson.sections.reduce((n, s) => n + s.objectives.length, 0);

  return (
    <div className="lesson-page textbook-layout">
      {/* Textbook Header */}
      <header className="lesson-chapter-header card">
        <div className="chapter-nav-row">
          <Link to={`/learn/${cid}`} className="breadcrumb-link">
            ← Return to Learning Pathway
          </Link>
          <span className="curriculum-badge">CAPS Module Unit</span>
        </div>

        <h1 className="chapter-title">{lesson.title}</h1>
        <p className="chapter-intro">
          <MathText inline content={lesson.description} />
        </p>

        {/* Clean Unboxed Metadata */}
        <div className="chapter-meta-line">
          <span className="meta-item">
            <strong>Difficulty:</strong> {lesson.difficulty}
          </span>
          <span aria-hidden="true" className="meta-dot">·</span>
          <span className="meta-item">
            <strong>Duration:</strong> ~{lesson.estimated_minutes} mins
          </span>
          <span aria-hidden="true" className="meta-dot">·</span>
          <span className="meta-item">
            <strong>Curriculum Objectives:</strong> {totalObjectives}
          </span>
          {lesson.tags.length > 0 && (
            <>
              <span aria-hidden="true" className="meta-dot">·</span>
              <span className="meta-item">{lesson.tags.slice(0, 3).join(", ")}</span>
            </>
          )}
        </div>
      </header>

      {/* Chapter Sections */}
      <main className="chapter-content-body">
        {lesson.sections.map((section, sIdx) => (
          <article key={section.id} className="card section-card">
            <header className="section-card-header">
              <span className="section-number">Section {sIdx + 1}</span>
              <h2 className="section-heading">{section.title}</h2>
            </header>

            {/* Key Concept Clickable Highlighters */}
            {section.key_concepts.length > 0 && (
              <div className="section-concept-bar">
                <span className="concept-bar-label">Core Concepts:</span>
                <div className="concept-buttons">
                  {section.key_concepts.map((concept) => (
                    <button
                      key={concept}
                      type="button"
                      className={`concept-pill ${
                        activeKeyword?.sectionId === section.id && activeKeyword?.concept === concept
                          ? "active"
                          : ""
                      }`}
                      onClick={() => handleConceptClick(section.id, concept)}
                      title={`Highlight "${concept}" in text`}
                    >
                      <MathText inline content={concept} />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Main Section Content & KaTeX */}
            <div
              className="textbook-body-text"
              ref={(el) => {
                sectionRefs.current[section.id] = el;
              }}
            >
              <MathText content={stripDiagrams(section.content)} />
            </div>

            {/* Mathematical Figures */}
            {section.figures.map((fig, fIdx) => (
              <figure key={fIdx} className="lesson-figure-wrapper">
                {fig.title && <figcaption className="figure-title">{fig.title}</figcaption>}
                <div className="figure-canvas-container">
                  <Figure spec={fig} height={320} maxWidth={520} />
                </div>
                {fig.caption && (
                  <figcaption className="figure-caption">{fig.caption}</figcaption>
                )}
              </figure>
            ))}

            {/* Step-by-Step Worked Examples */}
            {section.examples.length > 0 && (
              <section className="worked-examples-block">
                <h3 className="examples-section-title">Worked Model Solutions</h3>
                {section.examples.map((example, eIdx) => (
                  <WorkedExample key={eIdx} index={eIdx} content={stripDiagrams(example)} />
                ))}
              </section>
            )}

            {/* Section Learning Objectives */}
            {section.objectives.length > 0 && (
              <footer className="section-objectives-box">
                <h4 className="objectives-title">Assessed Competencies in this Unit</h4>
                <ul className="objectives-list">
                  {section.objectives.map((obj) => (
                    <li key={obj.id} className="objective-list-item">
                      <span className="bloom-tag">{obj.bloom_level}</span>
                      <span className="objective-text">
                        <MathText inline content={obj.description} />
                      </span>
                    </li>
                  ))}
                </ul>
              </footer>
            )}
          </article>
        ))}
      </main>

      {/* Sticky Bottom Study Action Dock */}
      <aside className="sticky-study-dock">
        <div className="dock-container">
          <div className="dock-text">
            <strong>Finished studying this unit?</strong>
            <span>Explain the concepts in your own words to achieve mastery.</span>
          </div>
          <div className="dock-actions">
            <Link to={`/learn/${cid}/quiz/${lid}`} className="btn btn-secondary btn-sm">
              Practice Quiz
            </Link>
            <Link to={`/learn/${cid}/dialog/${lid}`} className="btn btn-primary btn-sm">
              Start Socratic Dialogue →
            </Link>
          </div>
        </div>
      </aside>
    </div>
  );
}
